import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Crown, Repeat, Sparkles, ChevronDown, ChevronUp, Phone, MessageCircle, UserPlus } from "lucide-react";

const inputCls = "w-full rounded-xl border border-sand bg-cream px-4 py-2.5 text-sm text-obsidian placeholder:text-taupe/60 focus:outline-none focus:ring-2 focus:ring-gold";
import { API } from "@/config";

const eur = (n) => `${Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
const fmtMonth = (m) => new Date(m + "-01T12:00:00").toLocaleDateString("es-ES", { month: "short", year: "2-digit" });
const fmtDate = (d) => (d ? new Date(d + "T12:00:00").toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" }) : "—");

const LOYALTY = {
  fiel: { label: "Fiel", cls: "bg-gold text-white", Icon: Crown },
  recurrente: { label: "Repite", cls: "bg-rosesoft text-cocoa", Icon: Repeat },
  nueva: { label: "Nueva", cls: "bg-emerald-100 text-emerald-800", Icon: Sparkles },
  sin_visitas: { label: "Sin visitas aún", cls: "bg-sand/60 text-taupe", Icon: Sparkles },
};

export default function ClientsPanel({ headers }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/clients`, { headers })
      .then((res) => setData(res.data))
      .catch(() => toast.error("No se pudieron cargar las clientas"));
  }, []);

  const saveField = async (key, patch, msg = "Guardado") => {
    try {
      const res = await axios.put(`${API}/admin/clients/${key}`, patch, { headers });
      setData((d) => ({ ...d, clients: d.clients.map((c) => (c.key === key ? { ...c, ...res.data, registered: true } : c)) }));
      toast.success(msg);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    }
  };
  const saveNotes = (key, notes) => saveField(key, { notes }, "Notas guardadas");

  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState({ name: "", phone: "", email: "", notes: "" });
  const addClient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/clients`, draft, { headers });
      const res = await axios.get(`${API}/admin/clients`, { headers });
      setData(res.data);
      setShowAdd(false);
      setDraft({ name: "", phone: "", email: "", notes: "" });
      toast.success("Clienta guardada en tu agenda");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar la clienta");
    }
  };

  const thisMonth = new Date().toISOString().slice(0, 7);
  const months = useMemo(() => (data ? data.months.slice(-6) : []), [data]);
  const maxRevenue = Math.max(1, ...months.map((m) => m.revenue));
  const current = data?.months.find((m) => m.month === thisMonth);
  const filtered = useMemo(() => {
    if (!data) return [];
    const norm = (t) => (t || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const s = norm(q.trim());
    return data.clients.filter((c) => !s || norm(c.name).includes(s) || c.phone.replace(/\D/g, "").includes(s.replace(/\D/g, "") || "\u0000"));
  }, [data, q]);

  if (!data) return <div className="mt-6 h-48 rounded-2xl bg-sand/50 animate-pulse" />;

  const loyal = data.clients.filter((c) => c.loyalty === "fiel").length;

  return (
    <section data-testid="admin-clients-panel">
      <h1 className="font-display text-2xl sm:text-3xl font-semibold text-obsidian">Mi agenda de clientas</h1>
      <p className="text-sm text-taupe mt-1">
        Cada clienta se guarda por su teléfono con el nombre que puso la primera vez. Sus notas te llegarán con cada cita nueva que pida.
      </p>
      <button onClick={() => setShowAdd((v) => !v)} data-testid="client-add-btn" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-obsidian hover:bg-cocoa text-white text-sm font-medium px-5 py-2.5 transition-colors">
        <UserPlus className="w-4 h-4" /> Añadir clienta
      </button>
      {showAdd && (
        <form onSubmit={addClient} data-testid="client-add-form" className="mt-3 rounded-2xl bg-creamcard border border-sand p-5 grid sm:grid-cols-2 gap-3">
          <input required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Nombre" data-testid="client-add-name" className={inputCls} />
          <input required value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} placeholder="Teléfono" data-testid="client-add-phone" className={inputCls} />
          <input value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} placeholder="Correo (opcional)" data-testid="client-add-email" className={inputCls} />
          <input value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} placeholder="Notas (alergias, forma de uña, colores…)" data-testid="client-add-notes" className={inputCls} />
          <button type="submit" data-testid="client-add-submit" className="sm:col-span-2 w-fit rounded-xl bg-gold hover:bg-gold-dark text-white text-sm font-medium px-6 py-2.5 transition-colors">Guardar clienta</button>
        </form>
      )}

      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        <Stat testid="clients-stat-month" label="Este mes" value={eur(current?.revenue)} sub={`${current?.visits || 0} visita${current?.visits === 1 ? "" : "s"}`} />
        <Stat testid="clients-stat-total" label="Clientas" value={data.clients.length} sub={`${loyal} fiel${loyal === 1 ? "" : "es"} (3+ visitas)`} />
        <Stat testid="clients-stat-avg" label="Ticket medio" value={eur(current && current.visits ? current.revenue / current.visits : 0)} sub="este mes" />
      </div>

      {months.length > 0 && (
        <div className="mt-6 rounded-3xl bg-creamcard border border-sand p-6" data-testid="clients-months-chart">
          <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Ingresos por mes</p>
          <div className="flex items-end gap-3 mt-4 h-36">
            {months.map((m) => (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-1.5 min-w-0">
                <span className="text-xs text-obsidian font-medium">{eur(m.revenue)}</span>
                <div className="w-full rounded-t-lg bg-gold/80 transition-all" style={{ height: `${Math.max(6, (m.revenue / maxRevenue) * 90)}px` }} />
                <span className="text-[11px] text-taupe capitalize">{fmtMonth(m.month)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="relative mt-6">
        <Search className="w-4 h-4 text-taupe absolute left-4 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre o teléfono"
          data-testid="clients-search"
          className="w-full rounded-xl border border-sand bg-cream pl-11 pr-4 py-3 text-sm text-obsidian focus:outline-none focus:ring-2 focus:ring-gold"
        />
      </div>

      {filtered.length === 0 ? (
        <p data-testid="clients-empty" className="mt-6 text-sm text-taupe text-center py-10 rounded-2xl border border-dashed border-sand">Aún no hay clientas registradas.</p>
      ) : (
        <div className="grid gap-3 mt-4">
          {filtered.map((c) => {
            const L = LOYALTY[c.loyalty];
            const open = openKey === c.key;
            return (
              <article key={c.key} data-testid={`client-card-${c.key}`} className="rounded-2xl bg-creamcard border border-sand">
                <button onClick={() => setOpenKey(open ? null : c.key)} data-testid={`client-toggle-${c.key}`} className="w-full text-left p-5 flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[160px]">
                    <p className="font-medium text-obsidian flex items-center gap-2">
                      {c.name}
                      <span className={`inline-flex items-center gap-1 rounded-full text-[11px] px-2 py-0.5 ${L.cls}`}><L.Icon className="w-3 h-3" />{L.label}</span>
                    </p>
                    <p className="text-xs text-taupe mt-1">{c.phone} · Última visita: {fmtDate(c.last_visit)}{c.no_shows ? ` · ${c.no_shows} falta${c.no_shows > 1 ? "s" : ""}` : ""}</p>
                    {c.aliases?.length > 0 && <p data-testid={`client-aliases-${c.key}`} className="text-[11px] text-taupe mt-0.5">También ha reservado como: {c.aliases.join(", ")}</p>}
                    {c.notes && <p data-testid={`client-notes-preview-${c.key}`} className="text-xs text-amber-800 mt-1 truncate max-w-md">📝 {c.notes}</p>}
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <Mini label="Visitas" value={c.visits} />
                    <Mini label="Faltas" value={c.no_shows || 0} />
                    <Mini label="Total" value={eur(c.total)} />
                    <Mini label="Favorito" value={c.favorite_service || "—"} small />
                  </div>
                  {open ? <ChevronUp className="w-4 h-4 text-taupe" /> : <ChevronDown className="w-4 h-4 text-taupe" />}
                </button>
                {open && (
                  <div data-testid={`client-details-${c.key}`} className="px-5 pb-5 grid sm:grid-cols-2 gap-5 border-t border-sand pt-4">
                    <div className="sm:col-span-2 grid sm:grid-cols-3 gap-3">
                      <Field label="Nombre" testid={`client-name-${c.key}`} value={c.name} onSave={(v) => v.trim() && v !== c.name && saveField(c.key, { name: v })} />
                      <Field label="Teléfono" testid={`client-phone-${c.key}`} value={c.phone} onSave={(v) => v.trim() && v !== c.phone && saveField(c.key, { phone: v })} />
                      <Field label="Correo" testid={`client-email-${c.key}`} value={c.email} onSave={(v) => v !== c.email && saveField(c.key, { email: v })} />
                    </div>
                    <div>
                      <p className="text-xs tracking-[0.2em] uppercase text-gold font-medium">Servicios que usa</p>
                      <ul className="mt-2 text-sm text-cocoa space-y-1">
                        {Object.entries(c.services).sort((a, b) => b[1] - a[1]).map(([s, n]) => (
                          <li key={s} className="flex justify-between"><span>{s}</span><span className="text-taupe">{n}×</span></li>
                        ))}
                        {Object.keys(c.services).length === 0 && <li className="text-taupe">Todavía no ha venido</li>}
                      </ul>
                    </div>
                    <div>
                      <p className="text-xs tracking-[0.2em] uppercase text-gold font-medium">Gasto por mes</p>
                      <ul className="mt-2 text-sm text-cocoa space-y-1">
                        {Object.entries(c.monthly).sort((a, b) => b[0].localeCompare(a[0])).slice(0, 6).map(([m, v]) => (
                          <li key={m} className="flex justify-between capitalize"><span>{fmtMonth(m)}</span><span className="font-medium">{eur(v)}</span></li>
                        ))}
                      </ul>
                      <p className="text-xs text-taupe mt-3">Ticket medio: {eur(c.avg_ticket)} · Primera visita: {fmtDate(c.first_visit)}{c.upcoming ? ` · ${c.upcoming} cita próxima` : ""}</p>
                    </div>
                    <div className="sm:col-span-2">
                      <p className="text-xs tracking-[0.2em] uppercase text-gold font-medium">Notas de la clienta</p>
                      <p className="text-xs text-taupe mt-1">Alergias, forma de uña, colores favoritos… Se mostrarán en su próxima cita.</p>
                      <textarea
                        defaultValue={c.notes || ""}
                        onBlur={(e) => e.target.value.trim() !== (c.notes || "") && saveNotes(c.key, e.target.value)}
                        placeholder="Ej.: alérgica al acrílico · forma almond · le gusta el rosa empolvado"
                        rows={3}
                        data-testid={`client-notes-${c.key}`}
                        className="mt-2 w-full rounded-xl border border-sand bg-cream px-4 py-2.5 text-sm text-obsidian placeholder:text-taupe/60 focus:outline-none focus:ring-2 focus:ring-gold"
                      />
                      <p className="text-[11px] text-taupe mt-1">Se guarda automáticamente al salir del cuadro.</p>
                    </div>
                    <div className="sm:col-span-2 flex gap-2">
                      <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 rounded-full border border-sand text-taupe hover:text-obsidian text-xs px-3 py-1.5"><Phone className="w-3.5 h-3.5" /> Llamar</a>
                      <a href={`https://wa.me/${c.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-gold text-gold hover:bg-gold hover:text-white text-xs px-3 py-1.5 transition-colors"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl bg-creamcard border border-sand p-5">
      <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">{label}</p>
      <p className="font-display text-3xl text-obsidian mt-2">{value}</p>
      <p className="text-xs text-taupe mt-1">{sub}</p>
    </div>
  );
}

function Mini({ label, value, small }) {
  return (
    <div>
      <p className="text-[10px] tracking-[0.2em] uppercase text-taupe">{label}</p>
      <p className={`text-obsidian font-medium ${small ? "text-xs max-w-[110px] truncate" : "text-sm"}`}>{value}</p>
    </div>
  );
}

function Field({ label, value, onSave, testid }) {
  return (
    <label className="grid gap-1">
      <span className="text-[10px] tracking-[0.2em] uppercase text-taupe">{label}</span>
      <input defaultValue={value || ""} onBlur={(e) => onSave(e.target.value)} data-testid={testid} className={inputCls} />
    </label>
  );
}
